import { ActionContext, ActionTree } from "vuex";
import { RootState } from "@/store/types";
import { ApiCaller } from "@/ApiCaller";
import {
  IFlowState,
  IFlowSchedules,
  IFlowSchedule,
  FlowStep,
  FlowStatus,
} from "./types";

type FlowActionContext = ActionContext<IFlowState, RootState>;
type FlowActionTree = ActionTree<IFlowState, RootState>;

const api = ApiCaller.getInstance();

export const actions: FlowActionTree = {
  async start(context: FlowActionContext) {
    const response = await api.get("flow/start");
    const { goto } = response.data;

    if (goto === "flow/schedule") {
      await context.dispatch("fetchSchedules");
      context.commit("setEtl", true);
    } else {
      context.commit("setEtl", false);
    }
  },

  async fetchSchedules(context: FlowActionContext) {
    if (!context.state.schedules) {
      const { data } = await api.get("flow/schedule");

      if (data.goto && (data.goto === "done" || data.goto === "offline")) {
        context.commit("setDone", true);
      } else {
        const { sections } = data.schedule;
        const schedules = Object.values(sections as IFlowSchedules).reduce(
          (a: IFlowSchedules, c: IFlowSchedule) => ({
            ...a,
            [c.subject.toLowerCase()]: c,
          }),
          {}
        );

        context.commit("setSchedules", schedules);
      }
    }
  },

  async checkFlow(context: FlowActionContext) {
    if (!context.state.etl) {
      const { data } = await api.get("flow/next");
      const { wait } = data;

      context.commit("setEtl", data.etl);

      if (wait !== undefined && +wait <= 0) {
        const { etl, step } = context.state;

        if (etl && !step) {
          const { data } = await api.get("flow/next");

          const state: Partial<IFlowState> = {
            etl: data.etl,
            subject: data.subject,
            wait: data.wait || 0,
            step: FlowStep.Wait,
          };

          if (data.goto === "done") {
            state.step = FlowStep.Done;
          }

          context.commit("setSection", state);
        }
      }
    }
  },

  async startSection(context: FlowActionContext) {
    await context.dispatch("fetchSchedules");
    const { data } = await api.get("flow/next");

    const state: Partial<IFlowState> = {
      goto: data.goto,
      step: FlowStep.Wait,
      subject: data.subject,
    };

    context.commit("setSection", state);
  },

  async takeSection(context: FlowActionContext, status: FlowStatus) {
    const { goto } = context.state;

    if (goto) {
      context.commit("setTakeSection", {
        step: FlowStep.Section,
      });
    }
  },

  async checkSectionWaitTime(context: FlowActionContext) {
    const { data } = await api.get("flow/next");
    context.commit("setWait", data.wait);
  },

  async finishSection(context: FlowActionContext, subject: string) {
    const { data } = await api.get("flow/next");
    const { schedules } = context.state;

    if (schedules && subject) {
      const schedule = schedules[subject.toLowerCase()];
      schedule.is_completed = 1;
      context.commit("finishSection", {
        schedule,
        step: data.goto && data.goto === "done" ? FlowStep.Done : FlowStep.Wait,
        subject: subject.toLowerCase(),
      });
    } else if (data.goto && data.goto === "done") {
      context.commit("setDone", true);
    }
  },

  async previous(context: FlowActionContext, remainingTime: number) {
    const { data } = await api.get(`flow/previous/${remainingTime}`);
    const { subject } = context.state;

    if (data.url === "flow/next") {
      context.commit("setPrevious", {
        step: FlowStep.Wait,
        subject: subject,
      });
    } else {
      context.commit("setPrevious", {
        step: FlowStep.Review,
        subject: context.getters["getPreviousSubject"],
      });
    }
  },
};
